import { useEffect, useState } from 'react'
import { sendGet, sendPut } from '../../hooks/use-fetch'
import DataTable from '../../Components/Table/DataTable'
import { Modal } from '../../Components/Modal'
import { Column, ColumnType, type RowData } from '../../Components/Table/DataTable/DataTable.model'
import { type PaginationProps, usePagination } from '../../hooks/use-pagination'
import { Button, type SelectChangeEvent, Typography } from '@mui/material'
import { TextWithLabel } from '../../Components/TextWithLabel'
import { SelectWithLabel } from '../../Components/SelectWithLabel'

interface PriorityConfiguration {
  id: number
  districts: districtInfo[]
  defaultProviderID: number | null
  serviceProviders: Array<{
    id: number
    name: string
  }>
  priorityProgram: { id: number, name: string }
}
interface districtInfo {
  id: number
  districtName: string
  defaultProviderID?: number
}

export function PriorityProviderDefaultConfiguration (props: { isdid: number }): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const [data, setData] = useState<PriorityConfiguration>({ id: 0, districts: [], serviceProviders: [], priorityProgram: { id: 0, name: 'none' }, defaultProviderID: null })
  const [showModal, setShowModal] = useState(false)
  const [showDefaultModal, setShowDefaultModal] = useState(false)
  const [refreshTime, setRefreshTime] = useState<Date>(new Date())
  const [defaultPriorityProvider, setDefaultPriorityProvider] = useState<number | null>(null)
  const [editingRow, setEditingRow] = useState<{ id: number, districtName: string, defaultProviderID?: number }>({ id: 0, districtName: 'None' })
  const distirctNameColumn = new Column('districtName', 'District Name')
  const selectedProviderColumn = new Column('defaultProviderID', 'Default Provider', ColumnType.CUSTOM)
  selectedProviderColumn.customCellGenerator = row => {
    const val = data.serviceProviders.filter(i => i.id === (row.defaultProviderID ?? 0))
    if (val.length > 0) { return <>{val[0].name}</> }
    return <></>
  }
  const columns = [distirctNameColumn, selectedProviderColumn]

  useEffect(() => {
    const getData = async (): Promise<void> => {
      const { response: rsp } = await sendGet(`/ISDPrograms/GetPriorityConfigurationData?isdid=${props.isdid}`, {})
      const rspns: PriorityConfiguration = rsp
      rspns.serviceProviders = [{ id: 0, name: 'None' }, ...rsp.serviceProviders]
      setDefaultPriorityProvider(rsp.defaultProviderID)
      setData(rspns)
    }
    void getData()
  }, [refreshTime])
  const paginationProps: PaginationProps<RowData> = {
    rows: data?.districts ?? [],
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)
  const handleRowClick = (e: any, rowData: RowData): void => {
    setShowModal(true)
    const vals: { id: number
      districtName: string
      defaultProviderID?: number
    } = {
      id: rowData.id,
      districtName: rowData.districtName,
      defaultProviderID: rowData.defaultProviderID
    }
    setEditingRow(vals)
  }
  const handleUpdate = (e: SelectChangeEvent<number>): void => {
    const val: number = parseInt(e.target.value.toString())
    const newVals: districtInfo = { ...editingRow, defaultProviderID: val }
    setEditingRow(newVals)
  }
  const handleUpdateDefault = (e: SelectChangeEvent<number>): void => {
    const val: number = parseInt(e.target.value.toString())
    setDefaultPriorityProvider(val)
  }
  const handleSubmit = async (): Promise<void> => {
    const { response: rsp } = await sendPut('/ISDPrograms/SetPriorityConfiguration', editingRow)
    if (rsp === true) {
      setShowModal(false)
      setRefreshTime(new Date())
    } else {
      //
    }
  }

  const handleUpdateISDDefault = async (): Promise<void> => {
    const x = {
      id: data.id,
      defaultProviderID: defaultPriorityProvider
    }
    const { response: rsp } = await sendPut('/ISDPrograms/SetIsdDefault', x)
    if (rsp === true) {
      setShowDefaultModal(false)
      setRefreshTime(new Date())
    } else {
      //
    }
  }
  const onshowDefaultModal = (): void => {
    setShowDefaultModal(true)
    setDefaultPriorityProvider(data.defaultProviderID)
  }

  const selectedProvider = data.serviceProviders.filter(s => s.id === data.defaultProviderID)
  const selectedProviderName = selectedProvider.length > 0 ? selectedProvider[0].name : 'None'
  return <>
    <Typography variant='h5' >
      Waiver Routing
    </Typography>
    <Typography>Default:{selectedProviderName}</Typography>
    <Button onClick={onshowDefaultModal} >Edit</Button>
    <Modal
      onClose={() => { setShowDefaultModal(false) }}
      open={showDefaultModal}
      onConfirm={handleUpdateISDDefault}
      confirmationContent={<>
        <TextWithLabel label={'ISD Default'} name={'district name'} />
        <SelectWithLabel
          label='Selected Default Provider'
          options={data.serviceProviders}
          value={defaultPriorityProvider ?? 0}
          onChange={handleUpdateDefault}
          name={'provider value'}
        />
      </>}
    />

    <DataTable
      hasCheckbox={false}
      loading={false}
      columns={columns}
      rows={pagination.internalRows}
      name={'defaultProviderListTable'}
      page={pagination.page}
      totalRecords={pagination.recordCount}
      onPageChange={pagination.handleChangePage}
      onRowsPerPageChange={pagination.handleChangeRowsPerPage}
      onRowClick={handleRowClick}
    />
    <Modal
      onClose={() => { setShowModal(false) }}
      open={showModal}
      onConfirm={handleSubmit}
      confirmationContent={<>
        <TextWithLabel label={'District ' + editingRow.districtName} name={'district name'} />
        <SelectWithLabel
          label='Selected Default Provider'
          options={data.serviceProviders}
          value={editingRow?.defaultProviderID ?? 0}
          onChange={handleUpdate}
          name={'provider value'}
        />
      </>}
    />
  </>
}
